/**
 * selfServe.js — the web app's view of self-serve (sign-up) accounts.
 *
 * Sign-up: the request contract the sign-up pages send to
 * requestSelfServeAccount / completeSelfServeAccount, and the public sign-up
 * config they render from. The server side lives in functions/src/selfServe/
 * (accountDetails.js parses the payloads built here). The web app cannot
 * import from functions/, so the payload is pinned by a shared fixture
 * (functions/src/selfServe/__fixtures__/clientAccountPayloads.json) that both
 * sides test against.
 *
 * Shipping: a self-serve company carries companies/{id}.signup.source ===
 * 'self_serve', written only by server provisioning. Its people ship on the
 * platform's carrier accounts, so the app treats them differently in three ways:
 *
 *   1. Rating and booking. Any caller rating for a self-serve company uses the
 *      server: one getRatesUniversal call returns sell-only rates, each with a
 *      rateQuoteId, and bookRateUniversal books from that quote. The browser
 *      never prices, never writes shipmentRates and never sees carrier cost.
 *   2. What a member sees. Cost, markup and rate breakdowns are hidden, and so
 *      are the surfaces the server refuses self-serve callers (QuickShip,
 *      multi-leg and routing legs, quotes, pickups until approved, company and
 *      carrier management). Staff (admin, superadmin) browsing the company see
 *      the full app.
 *   3. Refusals. The server refuses with a reason code (details.reason);
 *      REASON_COPY turns each into plain-language text, names the sign-up
 *      page's next step (action) and says whether "Request higher limits"
 *      applies (review) or the same request may succeed later (retry).
 *
 * Pure functions only: nothing here talks to Firebase (the callable wrappers
 * live in src/services/selfServeService.js), so it is safe to unit-test.
 *
 * The reason codes mirror functions/src/selfServe/reasons.js: add new codes,
 * never rename or reuse one. The functions test
 * selfServe/__tests__/clientReasonsParity.test.js keeps the two lists in step.
 */

import { CANADIAN_PROVINCES } from '../constants/address';

// The Terms of Service and Privacy Policy version the pages at /terms and
// /privacy show. A request sends the version the person actually saw; the
// server refuses it (TERMS_OUTDATED) when its policy requires a newer one.
export const TERMS_VERSION = '2026-10-v1';

// Self-serve mail and support go to this mailbox (never a personal domain).
export const SUPPORT_EMAIL = 'tyler.murray@integratedcarriers.com';

export const INTAKE_MODES = Object.freeze({ CLOSED: 'closed', WAITLIST: 'waitlist', OPEN: 'open' });

// What the page renders when publicConfig/selfSignup is missing, unreadable or
// malformed: sign-up paused, no Google button. Fail closed.
export const CLOSED_CONFIG = Object.freeze({
    mode: INTAKE_MODES.CLOSED,
    googleEnabled: false,
    termsVersion: TERMS_VERSION,
    countries: Object.freeze(['CA']),
});

/**
 * Normalize the public mirror of the self-serve policy (publicConfig/selfSignup).
 * Anything unexpected reads as closed, so a typo can never open sign-up.
 */
export const normalizePublicConfig = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return CLOSED_CONFIG;
    if (!Object.values(INTAKE_MODES).includes(data.mode)) return CLOSED_CONFIG;
    const countries = Array.isArray(data.countries) && data.countries.length > 0
        && data.countries.every((c) => typeof c === 'string' && /^[A-Z]{2}$/.test(c))
        ? Object.freeze([...data.countries])
        : CLOSED_CONFIG.countries;
    return Object.freeze({
        mode: data.mode,
        // Google one-click only when the policy says so explicitly.
        googleEnabled: data.googleEnabled === true && data.mode === INTAKE_MODES.OPEN,
        termsVersion: typeof data.termsVersion === 'string' && data.termsVersion.trim()
            ? data.termsVersion.trim() : TERMS_VERSION,
        countries,
    });
};

// ── Form vocabulary ──────────────────────────────────────────────────────────
// The app's one list of provinces and territories (constants/address.js).
export const PROVINCES = Object.freeze(
    Object.entries(CANADIAN_PROVINCES).map(([code, name]) => Object.freeze({ code, name }))
);

// Exactly the values the server keeps (VOLUMES in accountDetails.js); any
// other value is dropped there, so offering one here would lose the answer.
export const MONTHLY_SHIPMENT_OPTIONS = Object.freeze([
    { value: '0-100', label: 'Up to 100' },
    { value: '100-500', label: '100 to 500' },
    { value: '1000+', label: '1,000 or more' },
]);

export const MIN_PASSWORD_LENGTH = 8;

// The server's checks (accountDetails.js isValidEmail / phoneDigits /
// normalizePostalCode), so the form flags what the server would refuse.
const EMAIL_RE = /^([^\s@"(),:;<>[\\\]]{1,64})@([a-z0-9-]+(?:\.[a-z0-9-]+)+)$/;
const POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/;

const isValidEmail = (value) => {
    const email = String(value || '').trim().toLowerCase();
    const m = email.length <= 254 ? EMAIL_RE.exec(email) : null;
    if (!m) return false;
    const labels = m[2].split('.');
    return labels.every((l) => l.length >= 1 && l.length <= 63 && !l.startsWith('-') && !l.endsWith('-'))
        && /^[a-z]{2,}$/.test(labels[labels.length - 1]);
};

/** 10 digits, or 11 starting with 1 (the country code). */
const isValidPhone = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const isValidPostalCode = (value) => POSTAL_RE.test(String(value || '').toUpperCase().replace(/[\s-]/g, ''));

// The form's fields carry the names the server reads (accountDetails.js), so a
// field the server refuses maps straight back onto the form.
export const EMPTY_BUSINESS = Object.freeze({
    firstName: '',
    lastName: '',
    phone: '',
    companyName: '',
    street: '',
    street2: '',
    city: '',
    province: '',
    postalCode: '',
    monthlyShipments: '',
    agreeToTerms: false,
});

/** "A1A1A1" / "a1a 1a1" -> "A1A 1A1". */
export const formatPostalCode = (value) => {
    const compact = String(value || '').replace(/[\s-]/g, '').toUpperCase();
    return compact.length === 6 ? `${compact.slice(0, 3)} ${compact.slice(3)}` : String(value || '').trim().toUpperCase();
};

// What a field says when it needs fixing, on the form and when the server
// refuses it (INVALID_REQUEST / TERMS_REQUIRED carry details.fields: the
// names, never the values).
const FIELD_PROBLEMS = Object.freeze({
    email: "That email address doesn't look right.",
    firstName: 'Check your first name.',
    lastName: 'Check your last name.',
    phone: 'Enter a 10-digit phone number.',
    companyName: 'Check the company name.',
    street: 'Check the street address.',
    street2: 'Check the unit or suite.',
    city: 'Check the city.',
    province: 'Choose a province or territory.',
    postalCode: 'Enter a Canadian postal code, like A1A 1A1.',
    agreeToTerms: 'Please accept the Terms of Service and Privacy Policy.',
});

/**
 * Field-level problems in the business details (and the email when
 * `withEmail`). An empty object means the form can be sent.
 */
export const businessErrors = (form, { withEmail = false } = {}) => {
    const errors = {};
    const t = (k) => String(form[k] || '').trim();
    if (withEmail) {
        if (!t('email')) errors.email = 'Enter your work email.';
        else if (!isValidEmail(t('email'))) errors.email = FIELD_PROBLEMS.email;
    }
    if (!t('firstName')) errors.firstName = 'Required';
    if (!t('lastName')) errors.lastName = 'Required';
    if (!t('phone')) errors.phone = 'Required';
    else if (!isValidPhone(t('phone'))) errors.phone = FIELD_PROBLEMS.phone;
    if (!t('companyName')) errors.companyName = 'Required';
    if (!t('street')) errors.street = 'Required';
    if (!t('city')) errors.city = 'Required';
    if (!PROVINCES.some((p) => p.code === t('province'))) errors.province = FIELD_PROBLEMS.province;
    if (!t('postalCode')) errors.postalCode = 'Required';
    else if (!isValidPostalCode(t('postalCode'))) errors.postalCode = FIELD_PROBLEMS.postalCode;
    if (form.agreeToTerms !== true) errors.agreeToTerms = FIELD_PROBLEMS.agreeToTerms;
    return errors;
};

/**
 * The form fields a refused self-serve call names (err.details.fields), as
 * field -> message, so the page can mark them. Names that are not form fields
 * (country, termsVersion, mode) are left to the general notice.
 */
export const serverFieldErrors = (err) => {
    const fields = err && err.details && Array.isArray(err.details.fields) ? err.details.fields : [];
    const errors = {};
    for (const f of fields) {
        if (typeof f === 'string' && Object.prototype.hasOwnProperty.call(FIELD_PROBLEMS, f)) errors[f] = FIELD_PROBLEMS[f];
    }
    return errors;
};

/**
 * The details both callables take (request mode adds `email`; session mode is
 * the Google finish): the FLAT form parseAccountDetails reads
 * (functions/src/selfServe/accountDetails.js), with the address fields at the
 * top level and the province as its 2-letter code. The server builds the
 * stored address itself. Canada only at launch: country is fixed to 'CA'.
 */
export const buildAccountDetails = (form) => {
    const t = (k) => String(form[k] || '').trim();
    return {
        firstName: t('firstName'),
        lastName: t('lastName'),
        phone: t('phone'),
        companyName: t('companyName'),
        street: t('street'),
        street2: t('street2'),
        city: t('city'),
        province: t('province').toUpperCase(),
        postalCode: formatPostalCode(t('postalCode')),
        country: 'CA',
        monthlyShipments: t('monthlyShipments'),
        agreeToTerms: form.agreeToTerms === true,
        termsVersion: TERMS_VERSION,
    };
};

/** requestSelfServeAccount payload. */
export const buildAccountRequest = (form) => ({
    email: String(form.email || '').trim().toLowerCase(),
    ...buildAccountDetails(form),
});

// ── Resending the activation email ───────────────────────────────────────────
// The server sends at most one email per recipient every 10 minutes and 3 a
// day (policy caps requestPerRecipient10Min / requestPerRecipientDay). A
// request inside a window gets the same { ok: true } and sends nothing, so the
// page must not offer Resend before the window is over: it waits the
// resendAfterSeconds the server returns (600 when absent or unusable).
export const DEFAULT_RESEND_AFTER_SECONDS = 600;
const MAX_RESEND_AFTER_SECONDS = 24 * 60 * 60;
/** The default daily cap per recipient (policy.js caps.requestPerRecipientDay). */
export const REQUESTS_PER_RECIPIENT_DAY = 3;

/** Seconds before Resend may send again, from a requestSelfServeAccount response. */
export const resendCooldownSeconds = (response) => {
    const s = response && typeof response === 'object' ? response.resendAfterSeconds : undefined;
    return Number.isFinite(s) && s > 0 && s <= MAX_RESEND_AFTER_SECONDS ? Math.ceil(s) : DEFAULT_RESEND_AFTER_SECONDS;
};

/** "9:05" for minutes, "42s" under a minute. */
export const formatWait = (seconds) => {
    const s = Math.max(0, Math.ceil(seconds));
    return s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${s}s`;
};

/** completeSelfServeAccount payload for the emailed link. */
export const buildLinkCompletion = ({ requestId, token, password }) => ({
    mode: 'link',
    requestId,
    token,
    password,
});

/** completeSelfServeAccount payload for a Google session (needsSetup). */
export const buildSessionCompletion = (form) => ({
    mode: 'session',
    ...buildAccountDetails(form),
});

// ─── Self-serve companies and who is viewing ─────────────────────────────────

export const SELF_SERVE_SOURCE = 'self_serve';

/** Roles that see carrier cost and run the back office (functions/src/utils/httpAuth.js ADMIN_ROLES). */
export const STAFF_ROLES = Object.freeze(['superadmin', 'super_admin', 'admin']);

const isStaffRole = (role) => STAFF_ROLES.includes(String(role || '').toLowerCase());

/** True for company data provisioned through self-serve. */
export function isSelfServeCompany(companyData) {
    return !!companyData && !!companyData.signup && companyData.signup.source === SELF_SERVE_SOURCE;
}

/**
 * Is a users/{uid} profile a self-serve tenant? Mirrors the server
 * (functions/src/selfServe/tenant.js isSelfServeProfile) and the rules
 * (firestore.rules isSelfServeProfile): the server-owned selfServeTenant flag,
 * or the profile's signupSource. Either marker counts (the restrictive answer).
 */
export function isSelfServeProfile(profile) {
    return !!profile && (profile.selfServeTenant === true || profile.signupSource === SELF_SERVE_SOURCE);
}

// The effective user's self-serve flag as AuthContext loaded it from
// users/{uid} (selfServeTenant: boolean), or undefined when the user object
// carries none (a role emulation, an emulation restored from an older session).
const profileFlag = (profile) => (profile && typeof profile.selfServeTenant === 'boolean'
    ? profile.selfServeTenant
    : undefined);

/**
 * Is the person looking at the app a member of a self-serve company (rather
 * than staff browsing one)? Decided from the effective user's profile
 * (AuthContext: users/{uid}.selfServeTenant, the server-owned flag the rules
 * and callables trust), so it is final as soon as the user is: it never waits
 * for the company context. A user object without the flag (a role emulation)
 * falls back to the company: a self-serve company plus a non-staff role.
 * @param {{userRole?: string, companyData?: object, profile?: object}} p
 */
export function isSelfServeViewer({ userRole, companyData, profile } = {}) {
    const flag = profileFlag(profile);
    if (flag !== undefined) return flag;
    return isSelfServeCompany(companyData) && !isStaffRole(userRole);
}

/**
 * Is isSelfServeViewer's answer final? Staff never are viewers, and a loaded
 * profile decides it at once. Without a profile flag it is final once the
 * company context has finished its first load: before that companyData is
 * null and a self-serve member would read as "not a viewer" (a later refresh
 * keeps companyData, so the answer stays final). Surfaces that would call what
 * the server refuses a member wait for it.
 */
export function isSelfServeViewerKnown({ userRole, companyData, companyLoading, profile } = {}) {
    return isStaffRole(userRole) || profileFlag(profile) !== undefined || !!companyData || !companyLoading;
}

// ─── What a self-serve member does not see ────────────────────────────────────

/**
 * Permissions (src/utils/rolePermissions.js PERMISSIONS values) whose surfaces
 * are hidden from a self-serve member whatever their role grants. Each is a
 * surface the server refuses them, or carrier cost:
 *   - use_quickship / select_quickship_carrier: QuickShip (manual carriers);
 *   - use_advanced_routing: multi-leg orders and routing legs;
 *   - create_quotes: quotes rate through the per-carrier callables;
 *   - view_shipment_costs / view_shipment_financials / view_rate_breakdown:
 *     carrier cost, markup and charge breakdowns;
 *   - edit_companies / create_divisions / edit_divisions: company docs are
 *     staff-write only;
 *   - manual_status_override / archive_shipment: a booked shipment's status
 *     changes only through the server (cancelShipment);
 *   - manage_brokers: customs brokers (self-serve ships within Canada);
 *   - view_follow_ups / view_followups: follow-up tasks (the nav entry, the
 *     /admin/followups route, the shipment detail button and the list badges);
 *     every follow-up callable refuses a self-serve member;
 *   - delete_customers: adminDeleteCustomer is staff-only; a member deactivates
 *     a customer instead;
 *   - view_companies: the companies admin (Accounts > Companies, /admin/companies):
 *     its list, owner and customer-count reads are staff queries the rules refuse
 *     a member, and a member's company is managed by staff;
 *   - view_reports: /reports, whose Generate Report calls generateReport, which
 *     refuses a self-serve member (SELF_SERVE_NOT_PERMITTED).
 * AdminRoute applies the same list to routes (canAccessRoute's `allows`), and
 * getShipmentDetailPermissions narrows the shipment detail page the same way.
 */
export const SELF_SERVE_HIDDEN_PERMISSIONS = Object.freeze([
    'use_quickship',
    'select_quickship_carrier',
    'use_advanced_routing',
    'create_quotes',
    'view_shipment_costs',
    'view_shipment_financials',
    'view_rate_breakdown',
    'edit_companies',
    'create_divisions',
    'edit_divisions',
    'manual_status_override',
    'archive_shipment',
    'manage_brokers',
    'view_follow_ups',
    'view_followups',
    'delete_customers',
    'view_companies',
    'view_reports',
]);

const HIDDEN = new Set(SELF_SERVE_HIDDEN_PERMISSIONS);

/**
 * Pages hidden from a self-serve member that no permission of their own gates
 * (the role reaches them through a permission a member also needs, such as the
 * admin home's view_admin_dashboard or create_shipments), so
 * SELF_SERVE_HIDDEN_PERMISSIONS cannot close them:
 *   - the API, which a self-serve account does not have (REST and MCP refuse a
 *     self-serve token, API_NOT_AVAILABLE; the key callables getApiProducts and
 *     createApiKey refuse a member, SELF_SERVE_NOT_PERMITTED):
 *       /admin/api-keys (Settings > API Keys), /admin/developer-portal (Settings >
 *       Developer Portal), /admin/integrations (the API playground, carrier
 *       sandbox and webhooks);
 *   - /create-shipment, the legacy shipment wizard: its Quick Ship and its
 *     per-carrier rating are refused to a member. A member books on the rated form
 *     instead (selfServeRedirectFor).
 * Each entry also closes the pages below it (/admin/api-keys/:keyId,
 * /create-shipment/:step). The menus leave out items linking to them, and the
 * route guards (AdminRoute, ProtectedRoute) send a member on.
 */
export const SELF_SERVE_HIDDEN_PAGES = Object.freeze([
    '/admin/api-keys',
    '/admin/developer-portal',
    '/admin/integrations',
    '/create-shipment',
]);

// Where a member closed out of a page is sent instead of the admin home.
const SELF_SERVE_PAGE_REDIRECTS = Object.freeze([
    ['/create-shipment', '/admin/shipments?action=rates'],
]);

/**
 * Where the route guards send a self-serve member from a page closed to them:
 * the legacy shipment wizard opens the rated shipment form (CreateShipmentX),
 * anything else the admin home.
 * @param {string} path  a pathname
 */
export function selfServeRedirectFor(path) {
    const p = String(path || '').split(/[?#]/)[0].replace(/\/+$/, '').toLowerCase();
    const hit = SELF_SERVE_PAGE_REDIRECTS.find(([page]) => p === page || p.startsWith(`${page}/`));
    return hit ? hit[1] : '/admin';
}

/**
 * Whether the self-serve overlay lets a page through (SELF_SERVE_HIDDEN_PAGES).
 * Never grants: route guards keep their role check and AND this onto it.
 * @param {string} path  a pathname (a query string or hash is ignored)
 * @param {{viewer: boolean}} ctx
 */
export function selfServeAllowsPage(path, { viewer = false } = {}) {
    if (!viewer) return true;
    const p = String(path || '').split(/[?#]/)[0].replace(/\/+$/, '').toLowerCase();
    return !SELF_SERVE_HIDDEN_PAGES.some((hidden) => p === hidden || p.startsWith(`${hidden}/`));
}

/**
 * Whether the self-serve overlay lets a permission through. Never grants: call
 * sites keep their role check and AND this onto it.
 * @param {string} permission  a PERMISSIONS value
 * @param {{viewer: boolean}} ctx
 */
export function selfServeAllows(permission, { viewer = false } = {}) {
    if (!viewer) return true;
    return !HIDDEN.has(permission);
}

/**
 * Whether a MENU shows a surface (the header's menus, its "+" and Settings).
 * Unlike selfServeAllows, a surface hidden from self-serve members stays hidden
 * until it is KNOWN whether the viewer is one (useSelfServe().known): until the
 * company context has loaded, a member reads as "not a viewer", and the header
 * would flash Carriers and Customs at them. Never grants: menus keep their role
 * check and AND this onto it.
 * @param {string} permission  a PERMISSIONS value
 * @param {{viewer: boolean, known: boolean}} ctx
 */
export function selfServeShows(permission, { viewer = false, known = true } = {}) {
    return selfServeAllows(permission, { viewer: viewer || !known });
}

/** selfServeShows for a page that no permission gates (SELF_SERVE_HIDDEN_PAGES). */
export function selfServeShowsPage(path, { viewer = false, known = true } = {}) {
    return selfServeAllowsPage(path, { viewer: viewer || !known });
}

/**
 * Where a "create" request lands for a self-serve member: QuickShip and quotes
 * are not available, so both open the rated shipment form instead.
 * @param {string} mode  'quickship' | 'quote' | 'advanced' | null
 */
export function selfServeCreateMode(mode, { viewer = false } = {}) {
    if (!viewer) return mode;
    return mode === 'quickship' || mode === 'quote' ? 'advanced' : mode;
}

// ─── Tier and limits ──────────────────────────────────────────────────────────

/** The launch defaults (functions/src/selfServe/policy.js DEFAULTS.tiers). */
export const DEFAULT_TIER_LIMITS = Object.freeze({
    starter: Object.freeze({ maxShipmentSell: 500, creditLimit: 1000, dailyBookings: 10, pickups: false }),
    approved: Object.freeze({ maxShipmentSell: 2500, creditLimit: 10000, dailyBookings: 50, pickups: true }),
});

const finiteOrNull = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * The company's tier, status and limits from its selfServe fields (the
 * per-company limits written at provisioning or by staff), falling back to the
 * tier defaults for a missing value. Null for a company that is not self-serve.
 */
export function selfServeAccount(companyData) {
    if (!isSelfServeCompany(companyData)) return null;
    const state = companyData.selfServe || {};
    const tier = ['starter', 'review', 'approved'].includes(state.tier) ? state.tier : 'review';
    const status = ['active', 'review', 'suspended'].includes(state.status) ? state.status : 'review';
    const defaults = tier === 'approved' ? DEFAULT_TIER_LIMITS.approved : DEFAULT_TIER_LIMITS.starter;
    const raw = state.limits || {};
    const limits = {
        maxShipmentSell: finiteOrNull(raw.maxShipmentSell) ?? defaults.maxShipmentSell,
        creditLimit: finiteOrNull(raw.creditLimit) ?? defaults.creditLimit,
        dailyBookings: finiteOrNull(raw.dailyBookings) ?? defaults.dailyBookings,
        pickups: typeof raw.pickups === 'boolean' ? raw.pickups : defaults.pickups,
    };
    return {
        tier,
        status,
        limits,
        canBook: status === 'active' && tier !== 'review',
        // Pickups only for an active account whose limits allow them.
        pickups: status === 'active' && tier !== 'review' && limits.pickups === true,
    };
}

/**
 * The "Add New Address" form on a self-serve shipment (CreateShipmentX). Every
 * self-serve tier ships within Canada only (the server refuses any other lane,
 * LANE_NOT_SUPPORTED), so a new address starts on Canada; and a shipment with no
 * customer saves its address to the company, so the customer is optional there.
 * Empty for any other company: its form is unchanged.
 * @param {{selfServe: boolean, hasCustomer: boolean}} p
 * @returns {{defaultCountry?: string, customerOptional?: boolean}}
 */
export function selfServeAddressFormProps({ selfServe = false, hasCustomer = false } = {}) {
    if (!selfServe) return {};
    return { defaultCountry: 'CA', customerOptional: !hasCustomer };
}

/** carriers/{id}.type values of a carrier that moves freight (LTL / FTL). */
const FREIGHT_CARRIER_TYPES = Object.freeze(['freight', 'hybrid']);

/**
 * The shipment types New Shipment offers a self-serve company, courier first
 * (so it is the default): courier always, freight only when the account has
 * pickups (a freight booking is a pickup; the booking gate refuses it
 * otherwise, PICKUPS_NOT_AVAILABLE) and an enabled connected carrier that moves
 * freight. A Starter account gets the one courier option, which the form
 * auto-selects with BOX packaging. The server rates only the carriers that move
 * the chosen type.
 * @param {object|null} account  selfServeAccount(companyData)
 * @param {Array<string|undefined>} connectedCarrierTypes  `type` of each enabled connected carrier doc
 */
export function selfServeShipmentTypes(account, connectedCarrierTypes = []) {
    const types = [{ value: 'courier', label: 'Courier' }];
    const movesFreight = (connectedCarrierTypes || []).some((t) => FREIGHT_CARRIER_TYPES.includes(String(t || '').toLowerCase()));
    if (account && account.pickups === true && movesFreight) types.push({ value: 'freight', label: 'Freight' });
    return types;
}

/** carrierIDs of the company's enabled connected carriers. */
export function enabledConnectedCarrierIDs(companyData) {
    const list = Array.isArray(companyData?.connectedCarriers) ? companyData.connectedCarriers : [];
    return [...new Set(list.filter((c) => c && c.enabled === true && c.carrierID).map((c) => c.carrierID))];
}

const CARRIER_NAMES = Object.freeze({
    CANADA_POST: 'Canada Post',
    CANADAPOST: 'Canada Post',
    MANITOULIN: 'Manitoulin',
    APEX: 'APEX',
    WCE: 'WCE',
});

/** Display names of the company's enabled connected carriers. */
export function connectedCarrierNames(companyData) {
    const list = Array.isArray(companyData?.connectedCarriers) ? companyData.connectedCarriers : [];
    const names = list
        .filter((c) => c && c.enabled === true && c.carrierID)
        .map((c) => CARRIER_NAMES[String(c.carrierID).toUpperCase()] || c.displayName || c.carrierName || c.carrierID);
    return [...new Set(names)];
}

const money = (n) => `CAD ${Number(n || 0).toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;
const moneyCents = (n) => `CAD ${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TIER_LABEL = Object.freeze({ starter: 'Starter', approved: 'Approved', review: 'Under review' });

/** e.g. "Starter: Canada Post domestic parcels, up to CAD 500 per shipment, CAD 1,000 outstanding, 10 bookings a day". */
export function tierSummary(companyData) {
    const account = selfServeAccount(companyData);
    if (!account) return '';
    const { limits } = account;
    const carriers = connectedCarrierNames(companyData);
    const carrierText = carriers.length ? `${carriers.join(', ')} shipments within Canada` : 'Shipments within Canada';
    return `${TIER_LABEL[account.tier]}: ${carrierText}, up to ${money(limits.maxShipmentSell)} per shipment, `
        + `${money(limits.creditLimit)} outstanding, ${limits.dailyBookings} bookings a day`;
}

/**
 * The credit picture from the server summary (getSelfServeAccountSummary):
 * outstanding = ledger exposure (booked sell x adjustment buffer, not yet
 * invoiced) + open invoice balances; remaining = limit - outstanding, never
 * below zero. Null when there is no summary.
 */
export function creditPicture(summary) {
    if (!summary || typeof summary !== 'object') return null;
    const limit = finiteOrNull(summary.limits?.creditLimit);
    if (limit === null) return null;
    const exposure = Math.max(0, finiteOrNull(summary.exposure) ?? 0);
    const openInvoices = Math.max(0, finiteOrNull(summary.openInvoices) ?? 0);
    const outstanding = Math.round((exposure + openInvoices) * 100) / 100;
    const remaining = Math.max(0, Math.round((limit - outstanding) * 100) / 100);
    return {
        creditLimit: limit,
        outstanding,
        remaining,
        usedRatio: limit > 0 ? Math.min(1, outstanding / limit) : 1,
        bookingsToday: Math.max(0, finiteOrNull(summary.bookingsToday) ?? 0),
        dailyBookings: finiteOrNull(summary.limits?.dailyBookings),
        text: `${moneyCents(remaining)} available of ${money(limit)}`,
    };
}

// ─── Refusal reasons ──────────────────────────────────────────────────────────

const UNDER_REVIEW = 'We are reviewing new accounts, usually within 1 business day. We will email you as soon as booking is available.';

/**
 * Client copy for each server reason code (functions/src/selfServe/reasons.js).
 * action: the sign-up page's next step: 'signup' (request a new link),
 *   'login', 'reload' or 'contact'.
 * review: offer "Request higher limits" (requestSelfServeReview).
 * retry: the same request may succeed later without any change.
 */
export const REASON_COPY = Object.freeze({
    // Availability
    NOT_AVAILABLE: { message: 'Self-serve accounts are not available yet.', action: 'contact' },
    SELF_SERVE_CLOSED: { message: 'New accounts are paused right now. Please check back soon.', action: 'contact' },
    WAITLIST_ONLY: { message: 'We have your request and will email you as soon as accounts open.' },
    // Raised by sign-up and by the booking gate, so it names neither.
    POLICY_UNAVAILABLE: { message: 'Self-serve accounts are temporarily unavailable. Please try again shortly.', retry: true },

    // Account request / completion (shown by the sign-up pages)
    COUNTRY_NOT_SUPPORTED: { message: 'SolushipX accounts are available in Canada only for now. US accounts are coming soon.' },
    TERMS_REQUIRED: { message: 'Please accept the Terms of Service and Privacy Policy.' },
    TERMS_OUTDATED: { message: 'Our terms have changed. Please reload the page and accept the current terms.', action: 'reload' },
    INVALID_REQUEST: { message: 'Some details are missing or invalid. Please check the form and try again.' },
    LINK_INVALID: { message: 'This activation link is not valid. Please request a new one.', action: 'signup' },
    LINK_EXPIRED: { message: 'This activation link has expired. Please request a new one.', action: 'signup' },
    LINK_LOCKED: { message: 'This activation link is locked after too many attempts. Please request a new one.', action: 'signup' },
    LINK_IN_USE: { message: 'This activation link is already being used. Please wait a moment and try again.', retry: true },
    PASSWORD_WEAK: { message: 'Please choose a stronger password: at least 8 characters and not a common password.' },
    ACCOUNT_EXISTS: { message: 'An account already exists for this email. Please sign in instead.', action: 'login' },
    ACCOUNT_DISABLED: { message: 'This account has been disabled. Please contact support.', action: 'contact' },
    STAFF_ACCOUNT: { message: 'Staff accounts cannot be used for a self-serve account.', action: 'login' },
    SESSION_NOT_ELIGIBLE: { message: 'This sign-in cannot be used to create an account. Please use the email link instead.', action: 'signup' },
    USE_EMAIL_LINK: { message: 'This email already has a password. Request an activation link with it to finish your account.', action: 'signup' },
    TRY_AGAIN_LATER: { message: 'We could not create your account right now. Please try again later.' },

    // Booking gate
    BOOKING_DISABLED: { message: 'Booking is paused for a short while. Please try again later.', retry: true },
    ACCOUNT_UNDER_REVIEW: { message: UNDER_REVIEW, review: true },
    ACCOUNT_SUSPENDED: { message: 'This account is suspended. Please contact support at tyler.murray@integratedcarriers.com.', action: 'contact' },
    QUOTE_REQUIRED: { message: 'Please get a new rate before booking.' },
    CARRIER_NOT_ALLOWED: { message: 'This carrier is not available on your account yet.', review: true },
    LANE_NOT_SUPPORTED: { message: 'Your account can ship within Canada only for now.', review: true },
    PICKUPS_NOT_AVAILABLE: { message: 'Pickups are not available on your account yet. Please drop the shipment off.', review: true },
    SHIPMENT_LIMIT: { message: 'This shipment is over the per-shipment limit for your account.', review: true },
    DAILY_LIMIT: { message: 'You have reached the daily booking limit for your account. You can book again tomorrow.', review: true },
    BOOKING_IN_PROGRESS: { message: 'Another booking is in progress. Please wait for it to finish, then try again.', retry: true },
    INVOICE_OVERDUE: { message: 'Booking is paused until your overdue invoice is paid.' },
    CREDIT_LIMIT: { message: 'This booking would go over your account limit. Pay an open invoice or request a higher limit.', review: true },
    // Deliberately the same as ACCOUNT_UNDER_REVIEW: the pool is never named.
    POOL_LIMIT: { message: UNDER_REVIEW, review: true },
    GATE_UNAVAILABLE: { message: 'We could not check your account limits. Please try again shortly.', retry: true },

    // Surfaces closed to self-serve
    SELF_SERVE_NOT_PERMITTED: { message: 'This feature is not available on self-serve accounts yet.' },
    SIGN_IN_AGAIN: { message: 'Your session has ended. Please sign in again.', action: 'login' },
    API_NOT_AVAILABLE: { message: 'API access is not available on self-serve accounts yet.' },

    // Review requests
    REVIEW_REQUEST_LIMIT: { message: 'You have already asked for a review today. We will be in touch.' },
    CARRIERS_UNAVAILABLE: { message: 'No carriers are available for your account right now. We are checking our carrier setup; please try again later.', retry: true },
});

/** The self-serve reason code a callable error carries, or null. */
export const selfServeReason = (err) => {
    const code = err && err.details && err.details.reason;
    return typeof code === 'string' && Object.prototype.hasOwnProperty.call(REASON_COPY, code) ? code : null;
};

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * A self-serve refusal from a callable error, or null when the error carries no
 * known reason code (the caller then shows its usual error).
 * @returns {{reason: string, message: string, canRequestReview: boolean, retryable: boolean} | null}
 */
export function describeSelfServeRefusal(error) {
    const reason = error && error.details && typeof error.details.reason === 'string' ? error.details.reason : null;
    const copy = reason && Object.prototype.hasOwnProperty.call(REASON_COPY, reason) ? REASON_COPY[reason] : null;
    if (!copy) return null;
    const details = error.details || {};
    let message = copy.message;
    if (reason === 'INVOICE_OVERDUE' && typeof details.invoiceNumber === 'string' && details.invoiceNumber.trim()) {
        message = `Pay invoice ${details.invoiceNumber.trim()} to keep shipping.`;
    } else if (reason === 'SHIPMENT_LIMIT' && num(details.limit) !== null) {
        message = `This shipment is over your account's limit of ${money(details.limit)} per shipment.`;
    } else if (reason === 'DAILY_LIMIT' && num(details.limit) !== null) {
        message = `You have reached your limit of ${details.limit} bookings a day. You can book again tomorrow.`;
    }
    return { reason, message, canRequestReview: copy.review === true, retryable: copy.retry === true };
}

// ── Google sessions that may finish an account (needsSetup) ──────────────────
/**
 * True for a signed-in session with no SolushipX profile that may finish a
 * self-serve account at /signup/finish (completeSelfServeAccount session mode):
 * a Google sign-in with a Google-verified email, no staff claim, and no
 * password on the Auth account (a password account finishes through the
 * emailed link instead, which proves the email). Every other profile-less
 * session is refused. The server re-checks all of this; this only decides
 * whether the page keeps the session or signs it out.
 *
 * @param {object} user        Firebase Auth user (uid, email, emailVerified, providerData)
 * @param {object} tokenResult its IdTokenResult (signInProvider, claims)
 */
export const isSetupEligibleSession = (user, tokenResult) => {
    if (!user || !tokenResult) return false;
    if (tokenResult.signInProvider !== 'google.com') return false;
    if (user.emailVerified !== true || typeof user.email !== 'string' || !user.email) return false;
    const claims = tokenResult.claims || {};
    if (claims.admin === true || claims.superadmin === true) return false;
    const providers = Array.isArray(user.providerData) ? user.providerData.map((p) => p && p.providerId) : [];
    if (providers.includes('password')) return false;
    return true;
};

// ─── Server rating ────────────────────────────────────────────────────────────

/**
 * The fields of a package line rated and hashed by the server (the quote's
 * request hash covers pieces, weight and dimensions with their units).
 */
const packageForRating = (p) => ({
    ...p,
    quantity: parseInt(p.packagingQuantity, 10) || 1,
    isNonStackable: p.isNonStackable || false,
});

/**
 * The getRatesUniversal request for a self-serve company: one call that rates
 * the company's connected carriers on the server and saves a quote per rate.
 *
 * `rateRequestData` is what the quote's request hash is computed from, so it
 * holds what bookRateUniversal hashes at booking: the same addresses, package
 * lines (all of them, as the booking sends them), shipment info and additional
 * services.
 *
 * @param {object} p
 * @param {string} p.companyDocId   companies/{id} (getRatesUniversal reads the company by doc id)
 * @param {object} p.shipFrom
 * @param {object} p.shipTo
 * @param {object[]} p.packages     normalized package lines (normalizePackageWeights)
 * @param {object} p.shipmentInfo
 * @param {object[]} [p.additionalServices]
 * @param {string|null} [p.customerId]     the shipment's customer (must match the draft's at booking)
 * @param {string|null} [p.shipmentDocId]  the draft, when one exists
 */
export function buildServerRatesRequest({
    companyDocId, shipFrom, shipTo, packages, shipmentInfo = {}, additionalServices = [], customerId = null, shipmentDocId = null,
}) {
    const lines = (Array.isArray(packages) ? packages : []).map(packageForRating);
    const services = Array.isArray(additionalServices) ? additionalServices : [];
    const info = { ...(shipmentInfo || {}), serviceLevel: 'any' };
    return {
        companyId: companyDocId,
        originAddress: shipFrom,
        destinationAddress: shipTo,
        packages: lines,
        shipmentInfo: { ...info, additionalServices: services },
        customerId: customerId || null,
        ...(shipmentDocId ? { shipmentDocId } : {}),
        rateRequestData: {
            shipFrom: hashedEndpoint(shipFrom),
            shipTo: hashedEndpoint(shipTo),
            packages: lines,
            shipmentInfo: info,
            additionalServices: services,
        },
    };
}

/**
 * An address as the booking's rate request carries it for the quote hash
 * (CreateShipmentX Origin / Destination): its postal code, else 'N/A', and its
 * country, else 'CA'.
 */
function hashedEndpoint(address) {
    const a = address && typeof address === 'object' ? address : {};
    return { ...a, postalCode: a.postalCode || 'N/A', country: a.country || 'CA' };
}

const firstText = (...vals) => vals.find((v) => typeof v === 'string' && v.trim()) || null;
const firstNumber = (...vals) => {
    for (const v of vals) {
        const n = typeof v === 'string' ? parseFloat(v) : v;
        if (typeof n === 'number' && Number.isFinite(n)) return n;
    }
    return null;
};

/**
 * The tax a server sell includes (the platform's GST/HST and QST on a self-serve
 * quote, functions/src/rates/pricing/platformTax.js), as { amount, lines }, or
 * null. Only well-formed positive lines are kept.
 */
function sellTax(rate) {
    const lines = (Array.isArray(rate?.tax?.lines) ? rate.tax.lines : [])
        .filter((l) => l && typeof l.code === 'string' && l.code && typeof l.amount === 'number' && Number.isFinite(l.amount) && l.amount > 0)
        .map((l) => ({ code: l.code, name: firstText(l.name, l.code), amount: Math.round(l.amount * 100) / 100 }));
    const amount = firstNumber(rate?.tax?.amount);
    return lines.length > 0 && amount !== null && amount > 0 ? { amount: Math.round(amount * 100) / 100, lines } : null;
}

/**
 * One server rate as the rate table shows it: carrier, service, transit and
 * the SELL price only, with the tax it includes (`tax`, when the server sent
 * it). Carrier cost, markup and the carrier's own charge lines are dropped even
 * if the server sent them. A rate without a quote id or a positive sell price is
 * not bookable and is left out.
 */
export function toSellOnlyRate(rate, index = 0) {
    if (!rate || typeof rate !== 'object' || typeof rate.rateQuoteId !== 'string' || !rate.rateQuoteId) return null;
    const sell = firstNumber(rate.serverPricing?.sell, rate.pricing?.total, rate.totalCharges, rate.total);
    if (sell === null || !(sell > 0)) return null;
    const currency = firstText(rate.pricing?.currency, rate.currency) || 'CAD';
    const carrier = rate.carrier && typeof rate.carrier === 'object' ? rate.carrier : {};
    const service = rate.service && typeof rate.service === 'object' ? rate.service : {};
    const transit = rate.transit && typeof rate.transit === 'object' ? rate.transit : {};
    const carrierName = firstText(rate.sourceCarrier?.name, carrier.name, rate.carrierName, typeof rate.carrier === 'string' ? rate.carrier : null) || 'Carrier';
    const carrierKey = firstText(rate.sourceCarrier?.system, rate.sourceCarrier?.key, carrier.key, rate.carrierID) || carrierName;
    const serviceName = firstText(service.name, rate.serviceName, typeof rate.service === 'string' ? rate.service : null, service.code, rate.serviceCode) || 'Standard';
    const serviceCode = firstText(service.code, rate.serviceCode, rate.serviceType);
    const transitDays = firstNumber(transit.days, rate.transitDays, rate.estimatedTransitDays, rate.transitTime);
    const delivery = firstText(transit.estimatedDelivery, transit.expectedDelivery, rate.estimatedDeliveryDate, rate.expectedDelivery, rate.deliveryDate);
    const tax = sellTax(rate);
    return {
        id: `quote_${rate.rateQuoteId}`,
        quoteId: rate.rateQuoteId,
        rateQuoteId: rate.rateQuoteId,
        carrierID: carrierKey,
        carrier: { name: carrierName, key: carrierKey, id: carrierKey, logo: firstText(carrier.logo, rate.carrierLogo) },
        sourceCarrier: { name: carrierName, key: carrierKey, system: carrierKey },
        service: { name: serviceName, code: serviceCode },
        serviceName,
        serviceCode,
        serviceLevel: firstText(rate.serviceLevel, service.level),
        secondaryServiceLevels: Array.isArray(rate.secondaryServiceLevels) ? rate.secondaryServiceLevels : [],
        transit: {
            days: transitDays ?? 0,
            estimatedDelivery: delivery,
            guaranteed: rate.transit?.guaranteed === true || rate.guaranteed === true,
        },
        transitDays: transitDays ?? 0,
        pricing: { total: sell, currency },
        totalCharges: sell,
        currency,
        ...(tax ? { tax } : {}),
        pricedBy: 'server',
        _order: index,
    };
}

/** The server's rates, sell only, cheapest first. */
export function toSellOnlyRates(availableRates) {
    return (Array.isArray(availableRates) ? availableRates : [])
        .map((r, i) => toSellOnlyRate(r, i))
        .filter(Boolean)
        .sort((a, b) => a.pricing.total - b.pricing.total || a._order - b._order);
}

/**
 * The booking request for a server-quoted rate: the quote id, never a
 * client-written shipmentRates doc.
 * @throws {Error} with details.reason 'QUOTE_REQUIRED' when the rate has no quote id
 */
export function buildServerBookingRequest({ rateRequestData, draftFirestoreDocId, selectedRate }) {
    const rateQuoteId = selectedRate && typeof selectedRate.rateQuoteId === 'string' ? selectedRate.rateQuoteId : null;
    if (!rateQuoteId) {
        throw Object.assign(new Error(REASON_COPY.QUOTE_REQUIRED.message), {
            code: 'functions/failed-precondition',
            details: { reason: 'QUOTE_REQUIRED' },
        });
    }
    return { rateRequestData, draftFirestoreDocId, rateQuoteId };
}

/**
 * The shipment draft fields a self-serve booking leaves to the server: the
 * booking writes the priced sell and cost from the quote (bookedPricingFields),
 * so the browser never writes cost or markup figures of its own.
 */
export const SERVER_PRICED_DRAFT_FIELDS = Object.freeze(['actualRates', 'markupRates']);

export function withoutServerPricedFields(shipmentData) {
    const out = { ...shipmentData };
    for (const f of SERVER_PRICED_DRAFT_FIELDS) delete out[f];
    return out;
}

/** The company's own default ship-from (address book), for a new shipment with no customer. */
export function companyDefaultShipFrom(addresses) {
    return (Array.isArray(addresses) ? addresses : [])
        .find((a) => a && a.addressClass === 'company' && a.isDefaultShipFrom === true) || null;
}

/**
 * What a booked shipment sells for (the price the member booked at): the
 * server-written sell (markupRates.totalCharges, else totalCharges). Never a
 * cost field. Null when the shipment has no positive sell yet.
 */
export function shipmentSellTotal(shipment) {
    if (!shipment || typeof shipment !== 'object') return null;
    const total = firstNumber(shipment.markupRates?.totalCharges, shipment.totalCharges);
    if (total === null || !(total > 0)) return null;
    const currency = firstText(shipment.markupRates?.currency, shipment.currency) || 'CAD';
    return { total: Math.round(total * 100) / 100, currency };
}
